const groupPrefix = createApiRoute(ApiModule.DIETARY, ApiDataModel.MEAL)
const historyGroupPrefix = createApiRoute(ApiModule.DIETARY)

/**
 * API functions for diet meals management.
 * @namespace apiMeals
 */
export const apiMeals = {
	/**
	 * Fetches a paginated list of meals.
	 */
	fetchDietMeals: async ({
		type,
		searchQuery,
		searchQueryType = 'name',
		page = 1,
		perPage = 25,
		filters,
		sorting,
		signal,
	}: {
		type: TDietMealType
		searchQuery: string
		searchQueryType?: 'name' | 'internal_name'
		page?: number
		perPage?: number
		filters?: IApiFilters
		sorting?: ISortingOption
		signal?: AbortSignal
	}): Promise<IApiResponseModel<IDietMealModel[]>> => {
		const search = createSearchQuery(searchQuery, searchQueryType)

		const { data, meta } = await apiCommon<IDietMeal[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery(
				{ page, perPage },
				{ meal_kind: type, meal_category: 'meal', ...filters, ...search },
				sorting ? { [sorting.key]: sorting.type } : undefined,
			),
			...(signal ? { signal } : {}),
		})
		return { data: data.map(DietMeal), meta }
	},
	/**
	 * Fetch single meal by ID.
	 */
	fetchDietMeal: async (id: string | number): Promise<IDietMealModel> => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}/${id}`, {
			method: 'GET',
		})
		return DietMeal(data)
	},
	/**
	 * Create new meal
	 */
	createDietMeal: async (body: IDietSharedMealPayloadCreate): Promise<IDietMealModel> => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}`, {
			method: 'POST',
			body,
		})
		return DietMeal(data)
	},
	/**
	 * Update existing meal model
	 */
	updateDietMeal: async (body: IDietSharedMealPayloadUpdate): Promise<IDietMealModel> => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}/${body.id}`, {
			method: 'PUT',
			body,
		})

		return DietMeal(data)
	},
	/**
	 * Duplicate existing meal model
	 */
	duplicateDietMeal: async (id: number) => {
		const { data } = await apiCommon<IDietMeal>(`${groupPrefix}/${id}/duplicate`, { method: 'POST' })

		return DietMeal(data)
	},
	/**
	 * Delete existing meal model
	 */
	deleteDietMeal: async (id: number, confirmed: boolean = false): Promise<void> => {
		await apiCommon(`${groupPrefix}/${id}`, {
			method: 'DELETE',
			...(confirmed ? createSimpleQuery({ confirmed: true }) : {}),
		})
	},

	/**
	 * Fetch additional meal variants information.
	 */
	fetchDietMealVariants: async (id: number) => {
		const { data } = await apiCommon<IDietMealVariant[]>(`${groupPrefix}/${id}/variants`, {
			method: 'GET',
		})
		return data.map(DietMealVariant)
	},
	/**
	 * Fetch meal production steps.
	 * @deprecated We don't use production step anymore - instead we use production stages
	 */
	fetchDietMealProductionSteps: async (id: number): Promise<IDietMealProductionStepModel[]> => {
		const { data } = await apiCommon<IDietProductionStep[]>(`${groupPrefix}/${id}/production-stages`, {
			method: 'GET',
		})

		return data.map(item => DietProductionStep(item))
	},
	/**
	 * Fetch meal menu usage dates.
	 */
	fetchMealMenuUsage: async (
		id: number,
		page: number = 1,
		filters?: { date_from?: string; date_to?: string },
	): Promise<IApiCommonModel<IMealMenuUsageDate[]>> =>
		await apiCommon<IMealMenuUsageDate[]>(`${groupPrefix}/${id}/menu-usage`, {
			method: 'GET',
			...createQuery({ page, perPage: 500 }, filters ? { ...filters } : undefined),
		}),
	/**
	 * Fetch meal or diet edit history.
	 */
	fetchMealEditHistory: async (
		type: EMealHistoryType.DIET | EMealHistoryType.MEAL,
		id: number,
		search: IApiSearch,
		{ page, perPage = 25 }: IApiPagination,
		filters?: IApiFilters,
	): Promise<IApiResponseModel<IDietaryActivityBusinessEventModel[]>> => {
		const searchFilter = createSearchQuery(search.query, search.type)
		const { data, meta } = await apiCommon<IDietaryActivityBusinessEvent[]>(
			`${historyGroupPrefix}/${type}/${id}/logs`,
			{
				method: 'GET',
				...createQuery({ page, perPage }, { ...searchFilter, ...filters }),
			},
		)

		return { data: data.map(DietaryActivityBusinessEvent), meta }
	},
}
